import { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import CustomDatePicker from '@/components/CustomDatePicker';
import GuestSelector from '@/components/GuestSelector';

type Variant = 'desktop' | 'mobile';

interface BookingSearchFormProps {
  variant?: Variant;
  className?: string;
  showTitle?: boolean;

  initialCheckIn?: Date | undefined;
  initialCheckOut?: Date | undefined;
  initialGuests?: string | undefined;

  onDateChange?: (
    checkIn: Date | undefined,
    checkOut: Date | undefined,
  ) => void;
  onGuestsChange?: (guests: string) => void;

  onSubmitted?: (payload: {
    checkIn?: Date;
    checkOut?: Date;
    guests?: string;
  }) => void;

  maxGuests?: number;
  unavailableDates?: Date[];

  showSubmit?: boolean;
  guestsDisabled?: boolean;
  syncOnChange?: boolean;
}

const addDaysLocal = (date: Date, days: number) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
};

const BookingSearchForm = ({
  variant,
  className = '',
  showTitle = true,
  maxGuests = 6,
  unavailableDates = [],
  onSubmitted,

  initialCheckIn,
  initialCheckOut,
  initialGuests,

  onDateChange,
  onGuestsChange,

  showSubmit,
  guestsDisabled = false,
  syncOnChange = true,
}: BookingSearchFormProps) => {
  const isMobileDevice = useIsMobile();
  const renderMobile = useMemo(
    () => (variant ? variant === 'mobile' : isMobileDevice),
    [variant, isMobileDevice],
  );

  const [checkIn, setCheckIn] = useState<Date | undefined>(initialCheckIn);
  const [checkOut, setCheckOut] = useState<Date | undefined>(initialCheckOut);
  const [guests, setGuests] = useState<string>(initialGuests ?? '1');

  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  useEffect(() => {
    setCheckIn(initialCheckIn);
  }, [initialCheckIn]);

  useEffect(() => {
    setCheckOut(initialCheckOut);
  }, [initialCheckOut]);

  useEffect(() => {
    if (typeof initialGuests === 'string') setGuests(initialGuests);
  }, [initialGuests]);

  const hasValidDateRange = !!checkIn && !!checkOut && checkOut > checkIn;

  const handleCheckInInternal = (date: Date | undefined) => {
    if (checkOut && date && checkOut <= date) {
      setCheckOut(undefined);
      if (syncOnChange) onDateChange?.(date, undefined);
    } else if (syncOnChange) {
      onDateChange?.(date, checkOut);
    }

    setCheckIn(date);

    if (!renderMobile && date) setCheckOutOpen(true);
    setCheckInOpen(false);
  };

  const handleCheckOutInternal = (date: Date | undefined) => {
    setCheckOut(date);
    if (syncOnChange) onDateChange?.(checkIn, date);
    setCheckOutOpen(false);
  };

  const handleGuestsInternal = (newGuests: string) => {
    setGuests(newGuests);
    if (syncOnChange) onGuestsChange?.(newGuests);
  };

  const doSubmit = () => {
    const payload = { checkIn, checkOut, guests };
    onSubmitted?.(payload);
    if (!syncOnChange) {
      onDateChange?.(checkIn, checkOut);
      if (guests) onGuestsChange?.(guests);
    }
  };

  const resolvedShowSubmit = useMemo(() => {
    if (typeof showSubmit === 'boolean') return showSubmit;
    return renderMobile;
  }, [showSubmit, renderMobile]);

  if (renderMobile) {
    return (
      <div className={`md:hidden space-y-4 ${className}`}>
        {showTitle && (
          <h2 className="text-xl font-semibold text-gray-900">
            Reserva tu estadía
          </h2>
        )}

        <div className="grid grid-cols-2 border border-gray-300 rounded-xl overflow-hidden">
          <div className="p-4 border-r border-gray-300">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              ENTRADA
            </div>
            <CustomDatePicker
              selected={checkIn}
              onSelect={handleCheckInInternal}
              placeholder="Agregar fecha"
              className="border-0 p-0 h-auto bg-transparent text-base text-gray-600"
              open={checkInOpen}
              onOpenChange={setCheckInOpen}
              unavailableDates={unavailableDates}
            />
          </div>

          <div className="p-4">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              SALIDA
            </div>
            <CustomDatePicker
              selected={checkOut}
              onSelect={handleCheckOutInternal}
              placeholder="Agregar fecha"
              className="border-0 p-0 h-auto bg-transparent text-base text-gray-600"
              minDate={checkIn}
              open={checkOutOpen}
              onOpenChange={setCheckOutOpen}
              unavailableDates={unavailableDates}
            />
          </div>
        </div>

        <div className="border border-gray-300 rounded-xl p-4">
          <div className="text-xs font-semibold text-gray-700 mb-2">
            HUÉSPEDES
          </div>
          <GuestSelector
            value={guests ?? '1'}
            onChange={handleGuestsInternal}
            maxGuests={maxGuests}
            className="border-0 p-0 h-auto bg-transparent text-base text-gray-600"
            placeholder="1 huésped"
            disabled={guestsDisabled}
          />
        </div>

        {resolvedShowSubmit && (
          <Button
            onClick={doSubmit}
            className="w-full h-12 text-white rounded-xl bg-brand-primary hover:bg-brand-primary-hover"
            disabled={!hasValidDateRange}
          >
            Buscar
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`hidden md:block ${className}`}>
      {showTitle && (
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Reserva tu estadía
        </h2>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Entrada
          </label>
          <CustomDatePicker
            selected={checkIn}
            onSelect={handleCheckInInternal}
            placeholder="Agregar fecha"
            unavailableDates={unavailableDates}
            open={checkInOpen}
            onOpenChange={setCheckInOpen}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Salida
          </label>
          <CustomDatePicker
            selected={checkOut}
            onSelect={handleCheckOutInternal}
            placeholder="Agregar fecha"
            open={checkOutOpen}
            onOpenChange={setCheckOutOpen}
            unavailableDates={unavailableDates}
            minDate={checkIn ? addDaysLocal(checkIn, 1) : new Date()}
          />
        </div>

        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Huéspedes
          </label>
          <GuestSelector
            value={guests ?? '1'}
            onChange={handleGuestsInternal}
            maxGuests={maxGuests}
            className="h-12"
            placeholder="Huéspedes"
            disabled={guestsDisabled}
          />
        </div>

        <div className="flex items-end">
          {resolvedShowSubmit ? (
            <Button
              onClick={doSubmit}
              className="w-full h-12 text-white rounded-xl bg-brand-primary hover:bg-brand-primary-hover"
              disabled={!hasValidDateRange}
            >
              Buscar
            </Button>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingSearchForm;
